// The Skill Builder picker list: SECTION, then the worksheets, and nothing in
// between.
//
// This replaced a Year > Cluster > Topic > Subtopic drill-down. Tutors were
// opening four accordions to reach one worksheet, and the levels in the middle
// were rarely the thing they were looking for. The curriculum path is still
// there, on the row, where it costs no clicks to read.
//
// Rows carry a checkbox so a tutor can take several in one go, which is how
// they actually work: a diagnostic that finds Measurement broken usually finds
// several Measurement skills broken together.
//
// A failed skill with no worksheet authored yet is still listed, greyed out and
// uncheckable. Hiding it would make a content gap look like a skill the student
// passed.

import React, { ReactNode } from "react"
import {
  BmdSkillbuilderRow,
  BmdSkillbuilderStrandGroup,
  isStageable,
} from "../utils/skillbuilder-rows"
import type { BmdSkillbuilderItem } from "../types/bmd-skillbuilder.types"
import { BMD_COLOURS } from "../constants/bmd.colours"

// ---------------------------------------------------------------------------
// Design tokens, from the PM's mockup
// ---------------------------------------------------------------------------

const INK = BMD_COLOURS.INK
// A step darker than they started. The QA kept reporting the small grey text
// across the Add Activity tabs as unreadable, so both moved together and the
// sibling tabs match BmdPrereqTabPanel. MUTED is still a clear step off INK,
// so the states that use it to mean "absent" or "disabled" still read that way.
const BODY = BMD_COLOURS.BODY
const MUTED = BMD_COLOURS.MUTED
const RULE = BMD_COLOURS.RULE
const SECTION_BG = BMD_COLOURS.SECTION_BG
const SELECTED_ROW_BG = BMD_COLOURS.SELECTED_ROW_BG
const ACCENT = BMD_COLOURS.ACCENT
/**
 * Checkbox size, set INLINE and deliberately.
 *
 * The theme sizes a bare `input[type=checkbox]` to roughly 34px, which turned
 * the picker into a column of large squares next to 13px text. A class would
 * have to out-specify that global rule; an inline size just wins.
 */
const CHECKBOX: React.CSSProperties = {
  width: 16,
  height: 16,
  margin: 0,
  accentColor: INK,
  cursor: "pointer",
  flexShrink: 0,
}

/** Column widths. Every row is a flex line, so these keep the columns aligned. */
const COL = {
  status: 120,
  score: 70,
  actions: 176,
} as const

/**
 * A worksheet is for one misconception; say which, in the tutor's language.
 * Short form, because it sits in a pill beside the skill name.
 */
export function worksheetFor(item: BmdSkillbuilderItem): string {
  if (item.misconception_type === "RECENTLY_LEARNED") return "Recently learned"
  if (item.misconception_type === "LACK_OF_DEPTH") return "Lack of depth"
  if (item.misconception_type === "FUNDAMENTAL") return "Fundamental"
  // A skip carries no misconception because the student never answered, which
  // is a fact about the attempt. "Not tagged" is a fact about the CONTENT, and
  // reads as a question someone forgot to label - on prod every wrong answer
  // is tagged, so every one of these was in fact a skip or a timeout.
  if (item.was_skipped) return "Not answered"
  return "Not tagged"
}

/** Each misconception gets its own tint, so the row is scannable by kind. */
function misconceptionStyle(item: BmdSkillbuilderItem): React.CSSProperties {
  if (item.misconception_type === "RECENTLY_LEARNED") {
    return { backgroundColor: "#e4edfa", color: "#2f5fa8" }
  }
  if (item.misconception_type === "LACK_OF_DEPTH") {
    return { backgroundColor: "#fbf1de", color: "#8a5a00" }
  }
  if (item.misconception_type === "FUNDAMENTAL") {
    return { backgroundColor: "#fbe4e8", color: "#a32338" }
  }
  return { backgroundColor: "#eef1f6", color: MUTED }
}

const Pill: React.FC<{
  children: ReactNode
  style?: React.CSSProperties
}> = ({ children, style }) => (
  <span
    style={{
      display: "inline-block",
      borderRadius: 20,
      padding: "2px 10px",
      fontSize: 11,
      fontWeight: 600,
      whiteSpace: "nowrap",
      ...style,
    }}
  >
    {children}
  </span>
)

export function statusLabel(
  item: BmdSkillbuilderItem,
  staged: boolean
): ReactNode {
  if (!item.has_skillbuilder) {
    return <span style={{ color: MUTED, fontSize: 12 }}>Not written yet</span>
  }
  // The VERDICT is read before the activity status: `passed` comes from a real
  // marked attempt, while the status flag is a separate write that can lag it.
  if (item.passed === false) {
    return (
      <Pill style={{ backgroundColor: "#fbf1de", color: "#8a5a00" }}>
        Needs review
      </Pill>
    )
  }
  if (item.status === "COMPLETED") {
    return (
      <Pill style={{ backgroundColor: "#e4f5ec", color: "#1c6b45" }}>
        Completed
      </Pill>
    )
  }
  if (item.status === "ASSIGNED" || staged) {
    return (
      <Pill style={{ backgroundColor: SECTION_BG, color: "#2f5fa8" }}>
        Assigned
      </Pill>
    )
  }
  return (
    <Pill style={{ backgroundColor: "#eef1f6", color: MUTED }}>
      Not assigned
    </Pill>
  )
}

/** A checkbox that can show the third, "some but not all" state. */
const TriCheckbox: React.FC<{
  checked: boolean
  indeterminate?: boolean
  disabled?: boolean
  onChange: () => void
  ariaLabel: string
}> = ({ checked, indeterminate = false, disabled, onChange, ariaLabel }) => {
  const ref = React.useRef<HTMLInputElement | null>(null)
  // `indeterminate` is a DOM property with no HTML attribute, so it cannot be
  // set through JSX and has to be written after each render.
  React.useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate && !checked
  }, [indeterminate, checked])
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      aria-label={ariaLabel}
      style={{ ...CHECKBOX, cursor: disabled ? "not-allowed" : "pointer" }}
      onClick={(e) => e.stopPropagation()}
    />
  )
}

type RowProps = {
  row: BmdSkillbuilderRow
  staged: boolean
  checked: boolean
  onToggleCheck: () => void
  onAdd: () => void
  onPreview: () => void
}

function SkillbuilderRow({
  row,
  staged,
  checked,
  onToggleCheck,
  onAdd,
  onPreview,
}: RowProps) {
  const item = row.item
  // Same rule as isStageable, but `staged` already arrives resolved for this
  // row, so there is no set to look it up in.
  const canStage =
    item.has_skillbuilder &&
    item.skillbuilder_id != null &&
    item.status === "NOT_ASSIGNED" &&
    !staged
  const isAdded = staged || item.status === "ASSIGNED"
  // A marked attempt under the pass mark is the tutor's job, not another
  // assignment: the row offers the answers instead of an Add button.
  const needsReview = item.passed === false

  return (
    <div
      className="d-flex align-items-center gap-3"
      style={{
        padding: "12px 14px",
        borderTop: `1px solid ${RULE}`,
        backgroundColor: checked ? SELECTED_ROW_BG : undefined,
      }}
    >
      <TriCheckbox
        checked={checked}
        disabled={!canStage}
        onChange={onToggleCheck}
        ariaLabel={`Select ${item.skill_name}`}
      />

      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <div
          style={{
            color: item.has_skillbuilder ? INK : MUTED,
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {item.skill_name}
        </div>
        <div className="d-flex align-items-center flex-wrap gap-2 mt-1">
          {item.has_skillbuilder ? (
            <Pill style={misconceptionStyle(item)}>{worksheetFor(item)}</Pill>
          ) : (
            <Pill style={{ backgroundColor: "#eef1f6", color: MUTED }}>
              No worksheet yet
            </Pill>
          )}
          <span style={{ color: MUTED, fontSize: 11.5 }}>
            {row.year_name}
            {row.sub_topics_name ? ` · ${row.sub_topics_name}` : ""}
            {item.depth > 0
              ? ` · ${item.depth} level${item.depth === 1 ? "" : "s"} below the test`
              : ""}
          </span>
        </div>
      </div>

      <div style={{ width: COL.status, flexShrink: 0 }}>
        {statusLabel(item, staged)}
      </div>
      <div
        style={{
          width: COL.score,
          flexShrink: 0,
          color: item.score_percent != null ? INK : MUTED,
          fontSize: 12.5,
          fontWeight: item.score_percent != null ? 700 : 400,
        }}
      >
        {item.score_percent != null ? `${item.score_percent}%` : "—"}
      </div>

      <div
        className="d-flex flex-shrink-0"
        style={{ width: COL.actions, justifyContent: "flex-end" }}
      >
        {needsReview ? (
          <button
            type="button"
            onClick={onPreview}
            style={{
              backgroundColor: "#fff",
              color: INK,
              border: `1px solid ${RULE}`,
              borderRadius: 6,
              padding: "7px 14px",
              fontSize: 12.5,
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            See answers
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={onAdd}
              disabled={!canStage && !isAdded}
              style={{
                backgroundColor: isAdded ? INK : canStage ? ACCENT : "#f0f2f6",
                color: isAdded || canStage ? "#fff" : MUTED,
                border: "none",
                borderRadius: "6px 0 0 6px",
                padding: "7px 14px",
                fontSize: 12.5,
                fontWeight: 600,
                whiteSpace: "nowrap",
                cursor: canStage ? "pointer" : "default",
              }}
            >
              {isAdded ? (
                <>
                  <i
                    className="mdi mdi-check"
                    style={{ fontSize: 12 }}
                    aria-hidden="true"
                  ></i>{" "}
                  Added
                </>
              ) : (
                "Add"
              )}
            </button>
            <button
              type="button"
              onClick={onPreview}
              disabled={!item.has_skillbuilder}
              style={{
                backgroundColor: "#fff",
                color: item.has_skillbuilder ? ACCENT : MUTED,
                border: `1px solid ${item.has_skillbuilder ? ACCENT : RULE}`,
                borderLeft: "none",
                borderRadius: "0 6px 6px 0",
                padding: "7px 14px",
                fontSize: 12.5,
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              Preview
            </button>
          </>
        )}
      </div>
    </div>
  )
}

type Props = {
  groups: BmdSkillbuilderStrandGroup[]
  stagedIds: Set<number>
  checkedIds: Set<number>
  /** Held open by a search, which has already cut the list down to matches. */
  forceOpen: boolean
  openStrand: string
  setOpenStrand: (key: string) => void
  onToggleCheck: (skillbuilderId: number) => void
  onToggleStrand: (group: BmdSkillbuilderStrandGroup) => void
  onAdd: (row: BmdSkillbuilderRow) => void
  onPreview: (item: BmdSkillbuilderItem) => void
}

export function BmdSkillbuilderStrandList({
  groups,
  stagedIds,
  checkedIds,
  forceOpen,
  openStrand,
  setOpenStrand,
  onToggleCheck,
  onToggleStrand,
  onAdd,
  onPreview,
}: Props) {
  return (
    <div>
      {groups.map((group) => {
        const key = String(group.strand_id ?? group.strand_name)
        const isOpen = forceOpen || openStrand === key

        const stageable = group.rows.filter((r) => isStageable(r, stagedIds))
        const checkedHere = stageable.filter(
          (r) =>
            r.item.skillbuilder_id != null &&
            checkedIds.has(r.item.skillbuilder_id),
        ).length
        const allChecked = stageable.length > 0 && checkedHere === stageable.length
        const assignedHere = group.rows.filter(
          (r) => r.item.status !== "NOT_ASSIGNED",
        ).length

        // Every worksheet in the section, no cap. It was capped at four with a
        // "show the other N" link; the PM wants the whole list, and the section
        // header already collapses it when a tutor wants it out of the way.
        const visibleRows = group.rows

        return (
          <div
            key={`strand-${key}`}
            style={{
              border: `1px solid ${RULE}`,
              borderTop: "none",
            }}
          >
            <div
              className="d-flex align-items-center gap-3"
              style={{
                backgroundColor: SECTION_BG,
                padding: "12px 14px",
                cursor: "pointer",
              }}
              onClick={() => setOpenStrand(isOpen ? "" : key)}
            >
              <TriCheckbox
                checked={allChecked}
                indeterminate={checkedHere > 0}
                disabled={stageable.length === 0}
                onChange={() => onToggleStrand(group)}
                ariaLabel={`Select all in ${group.strand_name}`}
              />
              <span style={{ color: INK, fontSize: 13, fontWeight: 700 }}>
                {group.strand_name}
              </span>
              <Pill style={{ backgroundColor: "#fff", color: BODY }}>
                {group.rows.length} skill builder
                {group.rows.length === 1 ? "" : "s"}
              </Pill>
              <span
                className="ms-auto"
                style={{ color: MUTED, fontSize: 12, whiteSpace: "nowrap" }}
              >
                {assignedHere} assigned
              </span>
              <i
                className={`mdi mdi-chevron-${isOpen ? "up" : "down"}`}
                style={{ color: MUTED, fontSize: 18 }}
                aria-hidden="true"
              ></i>
            </div>

            {isOpen ? (
              <div style={{ backgroundColor: "#fff" }}>
                {visibleRows.map((row) => {
                  const id = row.item.skillbuilder_id
                  const staged = id != null && stagedIds.has(id)
                  return (
                    <SkillbuilderRow
                      key={`${row.item.skill_id}-${row.year_level_id}`}
                      row={row}
                      staged={staged}
                      checked={id != null && checkedIds.has(id)}
                      onToggleCheck={() => id != null && onToggleCheck(id)}
                      onAdd={() => onAdd(row)}
                      onPreview={() => onPreview(row.item)}
                    />
                  )
                })}
              </div>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

export default BmdSkillbuilderStrandList
